import type { ReactNode } from "react";

import type { Store } from "../stores";

export interface SitemapProps {
  store?: Store;
  year?: string;
  month?: string;
  section?: string;
  notFound?: ReactNode;
}

export interface SitemapMonth {
  number: number;
  name: string;
}

export const sitemapMonths: SitemapMonth[] = [
  { number: 1, name: "month-january" },
  { number: 2, name: "month-february" },
  { number: 3, name: "month-march" },
  { number: 4, name: "month-april" },
  { number: 5, name: "month-may" },
  { number: 6, name: "month-june" },
  { number: 7, name: "month-july" },
  { number: 8, name: "month-august" },
  { number: 9, name: "month-september" },
  { number: 10, name: "month-october" },
  { number: 11, name: "month-november" },
  { number: 12, name: "month-december" },
];

export const getSitemapMonth = (month: string) => sitemapMonths[parseInt(month, 10) - 1]?.name ?? null;

export const sitemapMinYear = 2000;

export const isSitemapMonthValid = (month?: string) => {
  const parsed = parseInt(month ?? "", 10);
  return !isNaN(parsed) && parsed >= 1 && parsed <= 12;
};

export const isSitemapYearValid = (year?: string) => {
  const parsed = parseInt(year ?? "", 10);
  return !isNaN(parsed) && parsed >= sitemapMinYear && parsed <= new Date().getFullYear() + 1;
};

export const sitemapResultsMaxLimit = 50000;
export const sitemapResultsPerSection = 1250;

// Deep-offset sections are bounded server-side by IHRAR-12250, not here.
export const isSitemapSectionValid = (section?: string) => {
  const parsed = parseInt(section ?? "", 10);
  return Number.isFinite(parsed) && parsed >= 1 && Number.isFinite(parsed * sitemapResultsPerSection);
};

export enum SitemapPaths {
  SitemapBase = "/sitemap/",
  SitemapYearMonth = "/sitemap/:year(\\d{4})/:month(\\d{1,2})",
  SitemapYearMonthSection = "/sitemap/:year(\\d{4})/:month(\\d{1,2})/:section(\\d+)",
}

const sitemapYearMonthSegments = [":year", ":month"];
const sitemapYearMonthSectionSegments = [":year", ":month", ":section"];

// CMS sitemap routes carry no param constraints, so substitute the app's own constrained pattern.
// Matched by the actual year/month(/section) param names, not merely by segment count, so an
// unrelated CMS route under /sitemap/ with a different shape is never mistaken for one of these.
export const constrainSitemapPath = (path: string): string => {
  if (!path.startsWith(SitemapPaths.SitemapBase)) {
    return path;
  }
  const segments = path.slice(SitemapPaths.SitemapBase.length).split("/").filter(Boolean);
  if (
    segments.length === sitemapYearMonthSegments.length &&
    segments.every((s, i) => s === sitemapYearMonthSegments[i])
  ) {
    return SitemapPaths.SitemapYearMonth;
  }
  if (
    segments.length === sitemapYearMonthSectionSegments.length &&
    segments.every((s, i) => s === sitemapYearMonthSectionSegments[i])
  ) {
    return SitemapPaths.SitemapYearMonthSection;
  }
  return path;
};

export const isConstrainedSitemapPath = (path: string): boolean =>
  (Object.values(SitemapPaths) as string[]).includes(path);

// A digit-shaped route match (constrainSitemapPath) is not necessarily semantically valid.
export const isSitemapRouteMatchValid = (params: { year?: string; month?: string; section?: string }): boolean =>
  isSitemapYearValid(params.year) &&
  isSitemapMonthValid(params.month) &&
  (!params.section || isSitemapSectionValid(params.section));
